// pages/TaskManagement/TaskReportingByDate.js
//
// Legacy menu: "View My Task Reporting By Date" (TasksReviewDay)
// Round TM-8 (2026) — audit H-4: this is now for EVERY employee (their
// own day, legacy parity); managers additionally pick one subordinate
// (validated server-side against the transitive tree). Personal
// descriptions arrive masked when viewing someone else. Print replaces
// the legacy Crystal report.
// Backend: GET /api/TaskManagement/by-date?date=&empId=
//          GET /api/TaskManagement/my-subordinates

import React, { useCallback, useEffect, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table, Input, Label } from "reactstrap";
import { DashboardPageHeader, EmptyState, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-");
const fmtMins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;

const TaskReportingByDate = () => {
    document.title = "Task Reporting By Date || SSW Technologies Dashboard";

    const [date, setDate] = useState(todayStr());
    const [empId, setEmpId] = useState("");
    const [team, setTeam] = useState([]);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/TaskManagement/my-subordinates");
                const data = res?.data ?? res;
                setTeam(Array.isArray(data) ? data : []);
            } catch {
                setTeam([]);
            }
        })();
    }, []);

    const fetchReport = useCallback(async (d, e) => {
        try {
            setLoading(true);
            setError("");
            const params = { date: d };
            if (e) params.empId = e;
            const res = await api.get("/TaskManagement/by-date", { params });
            setPayload(res?.data ?? res);
        } catch (err) {
            setPayload(null);
            setError(err?.response?.data?.error || err?.response?.data?.message
                || "Unable to load the report.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReport(date, empId);
    }, [date, empId, fetchReport]);

    const rows = payload?.items || [];

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-calendar-line"
                    title="Task Reporting By Date"
                    subtitle="Every time-log entry for one day"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "By Date" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-end gap-3 mb-3 d-print-none">
                            <div>
                                <Label className="form-label mb-1">Date</Label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    style={{ width: "180px" }}
                                />
                            </div>
                            {team.length > 1 && (
                                <div>
                                    <Label className="form-label mb-1">Employee</Label>
                                    <Input
                                        type="select"
                                        value={empId}
                                        onChange={(e) => setEmpId(e.target.value)}
                                        style={{ width: "260px" }}
                                    >
                                        {team.map((t) => (
                                            <option key={t.empId} value={t.empId === team[0]?.empId ? "" : t.empId}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </Input>
                                </div>
                            )}
                            <div className="ms-auto d-flex align-items-center gap-2">
                                {payload && (
                                    <>
                                        <Badge color={payload.scope === "SELF" ? "info" : "warning"}>
                                            {payload.employeeName}
                                        </Badge>
                                        <Badge color="light" className="text-muted">
                                            Total: {fmtMins(payload.totalMinutes)}
                                        </Badge>
                                    </>
                                )}
                                <Button size="sm" color="light" onClick={() => window.print()}>
                                    <i className="ri-printer-line align-bottom me-1"></i>Print
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton rows={6} cols={8} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-calendar-close-line"
                                title="No entries"
                                message="No time was logged on this day."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Start</th>
                                            <th>End</th>
                                            <th>Task #</th>
                                            <th>Description</th>
                                            <th>Type</th>
                                            <th>Assigned By</th>
                                            <th>Job #</th>
                                            <th>Part #</th>
                                            <th className="text-end">Minutes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.id} className={r.invalid ? "table-danger" : ""}>
                                                <td>{fmtTime(r.start)}</td>
                                                <td>{r.end ? fmtTime(r.end) : <Badge color="warning">Open</Badge>}</td>
                                                <td className="fw-semibold">{r.taskId}</td>
                                                <td className="text-truncate" style={{ maxWidth: "340px" }}>{r.description}</td>
                                                <td>{r.taskType || "-"}</td>
                                                <td>{r.assignedByName}</td>
                                                <td>{r.jobNumber || "-"}</td>
                                                <td>{r.partNumber || "-"}</td>
                                                <td className="text-end">{r.minutes}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TaskReportingByDate;
